"use client";

import * as signalR from "@microsoft/signalr";
import { useEffect, useRef } from "react";

import { usePresenceStore } from "@/shared/model/presence-store";
import { useAuthStore } from "@/shared/model/auth-store";
import { useUserProfile } from "@/hooks/useAuth";
import { useUserActivity } from "@/shared/lib/hooks/use-user-activity";
import {
  getHubBase,
  setSignalRConnection,
  updateUserStatusViaSignalR,
} from "@/shared/lib/signalr-helpers";
import { isSignalRTransientNoise } from "@/shared/lib/signalr/transient-noise";

export function PresenceConnection() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const logout = useAuthStore((s) => s.logout);

  const { data: user, refetch: refetchUser } = useUserProfile();

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const stopRequestedRef = useRef(false);
  const startPromiseRef = useRef<Promise<void> | null>(null);
  const startAttemptRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const handlersRef = useRef<{
    InitialPresenceState?: (...args: unknown[]) => void;
    UserStatusChangedV2?: (...args: unknown[]) => void;
    UserActivityChanged?: (...args: unknown[]) => void;
    UserCustomStatusChanged?: (...args: unknown[]) => void;
  }>({});
  const userIdRef = useRef<string | null>(null);
  const ownStatusBeforeIdleRef = useRef<string | null>(null);
  // KAN-1684-followup: InitialPresenceState может прийти раньше, чем загрузится
  // профиль (userIdRef ещё null). Тогда ownStatus терялся, и после входа статус
  // «в сети» появлялся только после перезагрузки. Запоминаем и применяем позже.
  const pendingOwnStatusRef = useRef<string | null>(null);

  const setAll = usePresenceStore((s) => s.setAll);
  const setOnline = usePresenceStore((s) => s.setOnline);
  const setOffline = usePresenceStore((s) => s.setOffline);
  const setOnlineCount = usePresenceStore((s) => s.setOnlineCount);
  const setUserStatus = usePresenceStore((s) => s.setUserStatus);
  const setActivityContext = usePresenceStore((s) => s.setActivityContext);
  const setCustomStatus = usePresenceStore((s) => s.setCustomStatus);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
    const pending = pendingOwnStatusRef.current;
    if (user?.id && pending) {
      setUserStatus(user.id, pending, null);
      void refetchUser();
      pendingOwnStatusRef.current = null;
    }
  }, [user?.id, setUserStatus, refetchUser]);

  const waitForApiReady = async () => {
    if (process.env.NODE_ENV !== "development") return;
    const deadline = Date.now() + 15_000;
    while (Date.now() < deadline) {
      try {
        const res = await fetch("/api/health", {
          cache: "no-store",
          credentials: "include",
        });
        if (res.ok) return;
      } catch {
        // ignore
      }
      await new Promise((r) => setTimeout(r, 750));
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isInitialized || isLoading) return;

    if (!isAuthenticated) {
      stopRequestedRef.current = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (connectionRef.current) {
        const connToStop = connectionRef.current;
        if (handlersRef.current.InitialPresenceState) {
          connToStop.off("InitialPresenceState", handlersRef.current.InitialPresenceState);
        }
        if (handlersRef.current.UserStatusChangedV2) {
          connToStop.off(
            "UserStatusChangedV2",
            handlersRef.current.UserStatusChangedV2,
          );
        }
        if (handlersRef.current.UserActivityChanged) {
          connToStop.off("UserActivityChanged", handlersRef.current.UserActivityChanged);
        }
        if (handlersRef.current.UserCustomStatusChanged) {
          connToStop.off(
            "UserCustomStatusChanged",
            handlersRef.current.UserCustomStatusChanged,
          );
        }
        handlersRef.current = {};
        connToStop.stop();
        connectionRef.current = null;
        setSignalRConnection(null);
      }
      return;
    }

    let isMounted = true;
    stopRequestedRef.current = false;

    const scheduleRetry = () => {
      if (stopRequestedRef.current) return;
      if (retryTimerRef.current) return;
      const attempt = startAttemptRef.current;
      const base = Math.min(60_000, 1000 * 2 ** Math.min(attempt, 6));
      const jitter = Math.floor(Math.random() * 750);
      const delay = Math.min(60_000, base + jitter);
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        void ensureStarted();
      }, delay);
    };

    const ensureStarted = async () => {
      if (!isMounted || stopRequestedRef.current) return;
      const existing = connectionRef.current;
      if (existing?.state === signalR.HubConnectionState.Connected) return;
      if (startPromiseRef.current) return startPromiseRef.current;

      await waitForApiReady();
      const hubUrl = `${getHubBase()}/presence`;

      const retryPolicy: signalR.IRetryPolicy = {
        nextRetryDelayInMilliseconds: (ctx) => {
          const n = ctx.previousRetryCount;
          const base = n === 0 ? 0 : Math.min(60_000, 1000 * 2 ** Math.min(n, 6));
          return base + Math.floor(Math.random() * 750);
        },
      };

      const conn =
        existing ??
        new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            withCredentials: true,
            skipNegotiation: false,
            transport:
              signalR.HttpTransportType.WebSockets |
              signalR.HttpTransportType.ServerSentEvents,
          })
          .configureLogging(signalR.LogLevel.None)
          .withAutomaticReconnect(retryPolicy)
          .build();

      if (!existing) {
        connectionRef.current = conn;
        setSignalRConnection(conn);

        const removeOldHandlers = () => {
          if (handlersRef.current.InitialPresenceState) {
            conn.off("InitialPresenceState", handlersRef.current.InitialPresenceState);
          }
          if (handlersRef.current.UserStatusChangedV2) {
            conn.off("UserStatusChangedV2", handlersRef.current.UserStatusChangedV2);
          }
          if (handlersRef.current.UserActivityChanged) {
            conn.off("UserActivityChanged", handlersRef.current.UserActivityChanged);
          }
          if (handlersRef.current.UserCustomStatusChanged) {
            conn.off("UserCustomStatusChanged", handlersRef.current.UserCustomStatusChanged);
          }
        };

        removeOldHandlers();

        // Legacy события UserIsOnline/UserIsOffline удалены (этап 4.1):
        // UserStatusChangedV2 — единственный источник правды о presence,
        // его обработчик ниже сам управляет onlineUserIds через setOnline/setOffline.

        // Хендлеры хаба типизированы как (...args: unknown[]), поэтому payload
        // разбираем внутри через явный тип.
        handlersRef.current.InitialPresenceState = (...args: unknown[]) => {
          const data = args[0] as
            | {
                onlineCount?: number;
                onlineUserIds?: string[];
                ownStatus?: string | null;
                ownActivityContext?: string | null;
              }
            | undefined;
          if (!isMounted) return;
          if (data && typeof data.onlineCount === "number") {
            setOnlineCount(data.onlineCount);
          }
          if (data && Array.isArray(data.onlineUserIds)) {
            setAll(data.onlineUserIds.filter((x: unknown): x is string => typeof x === "string"));
          }
          if (data && typeof data.ownStatus === "string") {
            const currentUserId = userIdRef.current;
            if (currentUserId) {
              setUserStatus(currentUserId, data.ownStatus, null);
              void refetchUser();
            } else {
              // профиль ещё не пришёл — применим статус, когда появится user.id
              pendingOwnStatusRef.current = data.ownStatus;
            }
          }
          if (data && data.ownActivityContext !== undefined) {
            const currentUserId = userIdRef.current;
            if (currentUserId) {
              setActivityContext(
                currentUserId,
                typeof data.ownActivityContext === "string" ? data.ownActivityContext : null,
              );
            }
          }
        };
        conn.on("InitialPresenceState", handlersRef.current.InitialPresenceState);

        handlersRef.current.UserActivityChanged = (userId: unknown, activityContext: unknown) => {
          if (!isMounted) return;
          if (typeof userId !== "string") return;
          setActivityContext(
            userId,
            typeof activityContext === "string" ? activityContext : null,
          );
        };
        conn.on("UserActivityChanged", handlersRef.current.UserActivityChanged);

        const onUserStatusChangedV2: (...args: unknown[]) => void = (...args) => {
          if (!isMounted) return;
          const [userId, status, expiresAtUtc] = args;
          if (typeof userId !== "string" || typeof status !== "string") return;

          const normalizedExpires =
            expiresAtUtc == null
              ? null
              : expiresAtUtc instanceof Date
                ? expiresAtUtc.toISOString()
                : typeof expiresAtUtc === "string"
                  ? expiresAtUtc
                  : null;

          setUserStatus(userId, status, normalizedExpires);
          if (status === "offline") setOffline(userId);
          else setOnline(userId);
        };
        handlersRef.current.UserStatusChangedV2 = onUserStatusChangedV2;
        conn.on("UserStatusChangedV2", onUserStatusChangedV2);

        const onUserCustomStatusChanged: (...args: unknown[]) => void = (...args) => {
          if (!isMounted) return;
          const [userId, text, emoji, expiresAtUtc] = args;
          if (typeof userId !== "string") return;
          const normText = typeof text === "string" && text.length > 0 ? text : null;
          const normEmoji = typeof emoji === "string" && emoji.length > 0 ? emoji : null;
          const normExpires =
            expiresAtUtc == null
              ? null
              : expiresAtUtc instanceof Date
                ? expiresAtUtc.toISOString()
                : typeof expiresAtUtc === "string"
                  ? expiresAtUtc
                  : null;
          setCustomStatus(userId, normText, normEmoji, normExpires);
        };
        handlersRef.current.UserCustomStatusChanged = onUserCustomStatusChanged;
        conn.on("UserCustomStatusChanged", onUserCustomStatusChanged);

        conn.onclose(() => {
          startAttemptRef.current += 1;
          scheduleRetry();
        });
      }

      startAttemptRef.current += 1;
      startPromiseRef.current = conn
        .start()
        .then(() => {
          startAttemptRef.current = 0;
        })
        .catch(async (err) => {
          const msg = (err as Error)?.message ?? String(err);
          if (stopRequestedRef.current || isSignalRTransientNoise(err)) {
            return;
          }

          if (
            msg.includes("401") ||
            msg.includes("403") ||
            msg.toLowerCase().includes("unauthorized") ||
            msg.toLowerCase().includes("forbidden")
          ) {
            await logout();
            return;
          }

          scheduleRetry();
        })
        .finally(() => {
          startPromiseRef.current = null;
        });

      return startPromiseRef.current;
    };

    void ensureStarted();

    // bfcache (KAN-1173): открытый WebSocket запрещает браузеру сохранить страницу
    // в кэш «назад/вперёд» — возврат становится полной перезагрузкой. На pagehide
    // соединение закрываем (объект и обработчики остаются), на pageshow из кэша
    // поднимаем заново. Навигации роутера Next pagehide не вызывают.
    const onPageHide = () => {
      stopRequestedRef.current = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      void connectionRef.current?.stop();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      stopRequestedRef.current = false;
      startAttemptRef.current = 0;
      void ensureStarted();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      isMounted = false;
      stopRequestedRef.current = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      const connToStop = connectionRef.current;
      if (connToStop) {
        if (handlersRef.current.InitialPresenceState) {
          connToStop.off("InitialPresenceState", handlersRef.current.InitialPresenceState);
        }
        if (handlersRef.current.UserStatusChangedV2) {
          connToStop.off(
            "UserStatusChangedV2",
            handlersRef.current.UserStatusChangedV2,
          );
        }
        if (handlersRef.current.UserActivityChanged) {
          connToStop.off("UserActivityChanged", handlersRef.current.UserActivityChanged);
        }
        if (handlersRef.current.UserCustomStatusChanged) {
          connToStop.off(
            "UserCustomStatusChanged",
            handlersRef.current.UserCustomStatusChanged,
          );
        }
        handlersRef.current = {};
        connToStop.stop();
        connectionRef.current = null;
        setSignalRConnection(null);
      }
    };
  }, [
    isAuthenticated,
    isInitialized,
    isLoading,
    logout,
    refetchUser,
    setAll,
    setActivityContext,
    setCustomStatus,
    setOffline,
    setOnline,
    setOnlineCount,
    setUserStatus,
  ]);

  // Auto-Idle: через 5 минут бездействия → away (если был online).
  // Manual статусы (busy/playing/searching/invisible) не трогаем — пользователь явно их выбрал.
  useUserActivity({
    idleTimeout: 5 * 60 * 1000,
    onIdle: () => {
      if (!isAuthenticated) return;
      const currentUserId = userIdRef.current;
      if (!currentUserId) return;
      const currentStatus =
        usePresenceStore.getState().statusByUserId[currentUserId]?.status ?? null;
      if (currentStatus === "online" || currentStatus == null) {
        ownStatusBeforeIdleRef.current = currentStatus ?? "online";
        void updateUserStatusViaSignalR("away");
      }
    },
    onActive: () => {
      if (!isAuthenticated) return;
      const restored = ownStatusBeforeIdleRef.current;
      ownStatusBeforeIdleRef.current = null;
      if (!restored || restored === "away") return;
      // Возвращаем только если сейчас всё ещё away (юзер мог вручную поменять статус во время idle).
      const currentUserId = userIdRef.current;
      if (!currentUserId) return;
      const currentStatus =
        usePresenceStore.getState().statusByUserId[currentUserId]?.status ?? null;
      if (currentStatus === "away") {
        void updateUserStatusViaSignalR(restored);
      }
    },
  });

  return null;
}

